import { useEffect } from 'react'
import { Button, IconButton, Stack, styled, Typography } from '@mui/material'
import { useAudioPlayerStatus } from '@weblite/audio'
import { PlayIcon } from '$common/icons/play'
import { PauseIcon } from '$common/icons/pause'
import { CloseIcon } from '$common/icons/close'
import { FiveSecondBackwardIcon } from '$common/icons/five-second-backward'
import { formatDuration } from '$func/time'
import { toLocale } from '$func/text'
import { AUDIO_PLAYER_HEIGHT } from '$common/audio-player/audio-player-constants'
import { nothing } from '$helper/functions/utils'

const Container = styled(Stack)({
  zIndex: 3,
  backgroundColor: 'white',

  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',

  padding: '4px 12px',
  boxSizing: 'border-box',

  height: `${AUDIO_PLAYER_HEIGHT}px`,
  minHeight: `${AUDIO_PLAYER_HEIGHT}px`,

  width: '100%',
  overflow: 'hidden',
})

const SubContainer = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
}))

const SpeedButton = styled(Button)(({ theme }) => ({
  width: '41px',
  minWidth: '41px',
  height: '24px',

  color: theme.palette.secondary.main,

  borderColor: theme.palette.secondary.main,
  borderWidth: '1px',
  borderStyle: 'solid',
  borderRadius: '8px',
  padding: '2px 11px',
  boxSizing: 'border-box',
}))

const StyledIconButton = styled(IconButton)`
  padding: 0;
  width: 32px;
  height: 32px;
`

export const AudioPlayerApi = {
  pause: nothing,
}

export function AudioPlayer() {
  const {
    state,
    rate,
    duration,
    meta: { name, type },
    seekValue,
    setSeekValue,
    resume,
    pause,
    stop,
    setRate,
  } = useAudioPlayerStatus()

  useEffect(() => {
    AudioPlayerApi.pause = pause
    return () => {
      AudioPlayerApi.pause = nothing
    }
  }, [pause])

  const handleChangeRate = () => {
    if (rate === 1) setRate(1.5)
    else if (rate === 1.5) setRate(2)
    else setRate(1)
  }

  const elapsedTime = formatDuration(duration * seekValue * 1000)

  const handleFiveSecondBackward = () =>
    setSeekValue(Math.max(seekValue - 5 / duration, 0))

  const isBackwardable = type === 'VOICE'

  return (
    <Container dir="rtl">
      <SubContainer
        sx={{
          width: '100%',
          /* @noflip */
          marginLeft: '8px',
          overflowX: 'hidden',
        }}
      >
        <StyledIconButton onClick={stop}>
          <CloseIcon sx={{ color: 'neutral500' }} />
        </StyledIconButton>

        {/* Note: setting min width and max width to prevent change in width of typography */}
        <Typography
          variant="subtitle3"
          color="semiLightGray"
          sx={{ minWidth: '35px', maxWidth: '35px' }}
        >
          {elapsedTime}
        </Typography>

        <Typography
          variant="subtitle3"
          noWrap
          color="neutral900"
          sx={{ width: 'calc(100% - 85px)' }}
        >
          {name}
        </Typography>
      </SubContainer>

      <SubContainer sx={{ minWidth: isBackwardable ? '121px' : '24px' }}>
        {isBackwardable && (
          <SpeedButton onClick={handleChangeRate}>
            <Typography
              variant="caption1"
              color="secondary"
              gap="1px"
              display="flex"
              alignItems="center"
            >
              <Typography
                fontSize="9px"
                display="inline"
                color="inherit"
                fontWeight="700"
              >
                x
              </Typography>
              {toLocale(rate.toFixed(1))}
            </Typography>
          </SpeedButton>
        )}

        {isBackwardable && (
          <StyledIconButton>
            <FiveSecondBackwardIcon onClick={handleFiveSecondBackward} />
          </StyledIconButton>
        )}

        <StyledIconButton>
          {state !== 'PLAYING' ? (
            <PlayIcon sx={{ color: 'secondary.main' }} onClick={resume} />
          ) : (
            <PauseIcon sx={{ color: 'secondary.main' }} onClick={pause} />
          )}
        </StyledIconButton>
      </SubContainer>
    </Container>
  )
}
